import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "./AuthContext";

const MessagingContext = createContext(null);

export function MessagingProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const channelRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("conversations")
        .select("*")
        .or(`participant_one.eq.${user.id},participant_two.eq.${user.id}`)
        .order("last_message_at", { ascending: false });

      if (!error && data) {
        setConversations(data);
      }
    } catch (err) {
      console.warn("Failed to fetch conversations:", err);
    }
    setLoading(false);
  }, [user]);

  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id) { setUnreadCount(0); return; }
    try {
      const { data } = await supabase
        .from("conversations")
        .select("id")
        .or(`participant_one.eq.${user.id},participant_two.eq.${user.id}`);

      if (!data || data.length === 0) { setUnreadCount(0); return; }

      const convIds = data.map((c) => c.id);
      const { count } = await supabase
        .from("messages")
        .select("*", { count: "exact", head: true })
        .in("conversation_id", convIds)
        .neq("sender_id", user.id)
        .eq("read", false);

      setUnreadCount(count || 0);
    } catch (err) {
      console.warn("Failed to fetch unread count:", err);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user?.id) {
      fetchConversations();
      fetchUnreadCount();
    }
  }, [authLoading, user, fetchConversations, fetchUnreadCount]);

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("global-messages")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages" },
        () => {
          fetchConversations();
          fetchUnreadCount();
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "conversations" },
        () => {
          fetchConversations();
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [user, fetchConversations, fetchUnreadCount]);

  const getOrCreateConversation = async (expertId, expertName, expertAvatar) => {
    if (!user?.id) return null;

    const pOne = user.id < expertId ? user.id : expertId;
    const pTwo = user.id < expertId ? expertId : user.id;
    const pOneName = user.id < expertId ? (user.name || "User") : expertName;
    const pTwoName = user.id < expertId ? expertName : (user.name || "User");
    const pOneAvatar = user.id < expertId ? "" : expertAvatar;
    const pTwoAvatar = user.id < expertId ? expertAvatar : "";

    const { data: existing } = await supabase
      .from("conversations")
      .select("*")
      .eq("participant_one", pOne)
      .eq("participant_two", pTwo)
      .maybeSingle();

    if (existing) return existing;

    const { data: created, error } = await supabase
      .from("conversations")
      .insert({
        participant_one: pOne,
        participant_two: pTwo,
        participant_one_name: pOneName,
        participant_two_name: pTwoName,
        participant_one_avatar: pOneAvatar,
        participant_two_avatar: pTwoAvatar
      })
      .select()
      .single();

    if (!error && created) {
      await fetchConversations();
      return created;
    }
    return null;
  };

  const sendMessage = async (conversationId, content) => {
    if (!user?.id || !content.trim()) return null;

    const { data, error } = await supabase
      .from("messages")
      .insert({
        conversation_id: conversationId,
        sender_id: user.id,
        sender_name: user.name || "User",
        content: content.trim()
      })
      .select()
      .single();

    if (!error && data) {
      await supabase
        .from("conversations")
        .update({
          last_message: content.trim().substring(0, 100),
          last_message_at: new Date().toISOString()
        })
        .eq("id", conversationId);

      return data;
    }
    return null;
  };

  const getMessages = async (conversationId) => {
    const { data } = await supabase
      .from("messages")
      .select("*")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });

    return data || [];
  };

  const markAsRead = async (conversationId) => {
    if (!user?.id) return;
    await supabase
      .from("messages")
      .update({ read: true })
      .eq("conversation_id", conversationId)
      .neq("sender_id", user.id)
      .eq("read", false);

    await fetchUnreadCount();
  };

  const getPartnerInfo = useCallback((conversation) => {
    if (!user || !conversation) return { name: "", avatar: "" };
    if (conversation.participant_one === user.id) {
      return {
        id: conversation.participant_two,
        name: conversation.participant_two_name,
        avatar: conversation.participant_two_avatar
      };
    }
    return {
      id: conversation.participant_one,
      name: conversation.participant_one_name,
      avatar: conversation.participant_one_avatar
    };
  }, [user]);

  return (
    <MessagingContext.Provider
      value={{
        conversations,
        unreadCount,
        loading,
        getOrCreateConversation,
        sendMessage,
        getMessages,
        markAsRead,
        getPartnerInfo,
        fetchConversations,
        fetchUnreadCount
      }}
    >
      {children}
    </MessagingContext.Provider>
  );
}

export function useMessaging() {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error("useMessaging must be used within a MessagingProvider");
  }
  return context;
}
